import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Statistic, Table, Tag, Space, Typography, Spin, Alert,
  Button, Modal, Form, Input, Select, DatePicker, InputNumber, message, Empty
} from 'antd';
import {
  FileSearchOutlined, PlusOutlined, ProjectOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { rfpService, userService } from '../services/api';
import '../styles/Phase5.css';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const STATUS_CONFIG = {
  draft: { color: 'default', label: 'Draft' },
  requirements_review: { color: 'blue', label: 'Requirements Review' },
  scoring: { color: 'processing', label: 'Scoring' },
  evaluation: { color: 'purple', label: 'Evaluation' },
  decision: { color: 'orange', label: 'Decision' },
  closed: { color: 'green', label: 'Closed' },
};

const TYPE_CONFIG = {
  rfp: { color: 'blue', label: 'RFP' },
  rfi: { color: 'cyan', label: 'RFI' },
  vendor_selection: { color: 'green', label: 'Vendor Selection' },
};

const RFPProjects = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    draft: 0,
  });

  // Users for owner select
  const [users, setUsers] = useState([]);

  // Creation modal
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const projectsData = await rfpService.getProjects();
      setProjects(projectsData);

      // Compute stats from projects
      const total = projectsData.length;
      const active = projectsData.filter(
        (p) => p.status === 'scoring' || p.status === 'evaluation'
      ).length;
      const completed = projectsData.filter((p) => p.status === 'closed').length;
      const draft = projectsData.filter((p) => p.status === 'draft').length;

      setStats({ total, active, completed, draft });
      setError(null);
    } catch (err) {
      setError('Failed to load RFP projects');
      message.error('Failed to load RFP projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    userService.getAll().then(setUsers).catch(() => {});
  }, [fetchData]);

  const handleCreateProject = async () => {
    try {
      const values = await createForm.validateFields();
      setCreating(true);
      const payload = {
        ...values,
        deadline: values.deadline ? values.deadline.format('YYYY-MM-DD') : null,
      };
      await rfpService.createProject(payload);
      message.success('Project created successfully');
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchData();
    } catch (err) {
      if (err.errorFields) {
        return; // Form validation error
      }
      message.error('Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: 'Type',
      dataIndex: 'project_type',
      key: 'project_type',
      width: 140,
      render: (val) => {
        const cfg = TYPE_CONFIG[val];
        return cfg ? (
          <Tag color={cfg.color}>{cfg.label}</Tag>
        ) : (
          <Tag>{val}</Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 160,
      render: (val) => {
        const cfg = STATUS_CONFIG[val];
        return <Tag color={cfg?.color || 'default'}>{cfg?.label || val}</Tag>;
      },
    },
    {
      title: 'Requirements',
      dataIndex: 'total_requirements',
      key: 'total_requirements',
      width: 120,
      render: (val) => val || 0,
    },
    {
      title: 'Candidates',
      dataIndex: 'total_candidates',
      key: 'total_candidates',
      width: 110,
      render: (val) => val || 0,
    },
    {
      title: 'Deadline',
      dataIndex: 'deadline',
      key: 'deadline',
      width: 120,
      render: (val) => (val ? new Date(val).toLocaleDateString() : '-'),
    },
    {
      title: 'Owner',
      dataIndex: 'owner_name',
      key: 'owner_name',
      width: 140,
      render: (val) => val || '-',
    },
  ];

  if (loading && projects.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <ProjectOutlined style={{ marginRight: 8 }} />
          RFP Projects
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
          size="large"
        >
          New Project
        </Button>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Projects"
              value={stats.total}
              prefix={<ProjectOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active"
              value={stats.active}
              prefix={<FileSearchOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Completed"
              value={stats.completed}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Draft"
              value={stats.draft}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Projects Table */}
      <Card>
        {projects.length === 0 && !loading ? (
          <Empty
            description="No RFP projects yet. Click 'New Project' to get started."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            dataSource={projects}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} projects` }}
            onRow={(record) => ({
              onClick: () => navigate(`/rfp/${record.id}`),
              style: { cursor: 'pointer' },
            })}
          />
        )}
      </Card>

      {/* Create Project Modal */}
      <Modal
        title="New RFP Project"
        open={createModalVisible}
        onOk={handleCreateProject}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        okText="Create"
        confirmLoading={creating}
        width={600}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter a project title' }]}
          >
            <Input placeholder="Enter project title" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Describe the project objectives..." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="project_type"
                label="Project Type"
                rules={[{ required: true, message: 'Please select a project type' }]}
              >
                <Select placeholder="Select type">
                  <Option value="rfp">RFP</Option>
                  <Option value="rfi">RFI</Option>
                  <Option value="vendor_selection">Vendor Selection</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="deadline"
                label="Deadline"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="owner_user_id"
                label="Owner"
              >
                <Select
                  placeholder="Select owner (defaults to you)"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {users.map((u) => (
                    <Option key={u.id} value={u.id}>
                      {u.full_name || u.email}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="scoring_scale"
                label="Scoring Scale"
                initialValue={5}
              >
                <Select>
                  <Option value={3}>1-3</Option>
                  <Option value={5}>1-5</Option>
                  <Option value={10}>1-10</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default RFPProjects;
